/**
 * Autosave with a small status indicator in the header.
 *
 * Typing schedules a save a short moment later, so a burst of keystrokes causes
 * one write rather than dozens. Anything structural (ticking a box, adding a
 * row, leaving a field) saves immediately.
 */

const DEBOUNCE_MS = 500;
let clearTimer = null;

/** Update the "Saved" indicator. */
export function setSaveStatus(state, message) {
  const node = document.getElementById('saveStatus');
  if (!node) return;
  clearTimeout(clearTimer);
  node.dataset.state = state;
  node.textContent = message ?? { saving: 'Saving…', saved: 'Saved', error: 'Not saved', idle: '' }[state] ?? '';
  if (state === 'saved') {
    // Fade the message away so it does not nag, but leave errors on screen.
    clearTimer = setTimeout(() => {
      if (node.dataset.state === 'saved') {
        node.dataset.state = 'idle';
        node.textContent = '';
      }
    }, 2500);
  }
}

/**
 * Create an autosave controller around a save function.
 *   const save = createAutosave(() => store.saveDay(day));
 *   save.schedule();  // debounced, for keystrokes
 *   save.now();       // immediate, for structural changes
 */
export function createAutosave(saveFn) {
  let timer = null;
  let pending = null;

  async function run() {
    clearTimeout(timer);
    timer = null;
    setSaveStatus('saving');
    try {
      await saveFn();
      setSaveStatus('saved');
    } catch (err) {
      console.error('Save failed', err);
      setSaveStatus('error', 'Not saved');
    }
  }

  return {
    schedule() {
      clearTimeout(timer);
      setSaveStatus('saving');
      timer = setTimeout(() => {
        pending = run();
      }, DEBOUNCE_MS);
    },
    now() {
      pending = run();
      return pending;
    },
    /** Flush any pending save, e.g. when leaving the view. */
    async flush() {
      if (timer) await this.now();
      else if (pending) await pending;
    },
    cancel() {
      clearTimeout(timer);
      timer = null;
    },
  };
}
